import { useCallback, useEffect, useMemo, useState } from "react";

/**
 * Reusable batch-selection state for hub/list screens.
 *
 * @param {Array} items - Visible selectable rows
 * @param {(item: unknown) => string} getItemId - Stable id extractor
 * @param {{ enabled?: boolean }} options - When false, selection is cleared
 */
export function useBatchSelection(items, getItemId, { enabled = true } = {}) {
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const selectableIds = useMemo(
    () => items.map((item) => getItemId(item)),
    [items, getItemId],
  );

  const selectableIdSet = useMemo(
    () => new Set(selectableIds),
    [selectableIds],
  );

  useEffect(() => {
    if (!enabled) {
      setSelectedIds(new Set());
      return;
    }

    setSelectedIds((previous) => {
      const next = new Set(
        [...previous].filter((id) => selectableIdSet.has(id)),
      );
      if (next.size === previous.size) return previous;
      return next;
    });
  }, [enabled, selectableIdSet]);

  const selectedCount = useMemo(() => {
    if (!enabled) return 0;
    return [...selectedIds].filter((id) => selectableIdSet.has(id)).length;
  }, [enabled, selectedIds, selectableIdSet]);

  const allSelected =
    enabled &&
    selectableIds.length > 0 &&
    selectableIds.every((id) => selectedIds.has(id));

  const someSelected = enabled && selectedCount > 0;

  const isSelected = useCallback(
    (id) => enabled && selectedIds.has(id),
    [enabled, selectedIds],
  );

  const toggleItem = useCallback(
    (id) => {
      if (!enabled) return;

      setSelectedIds((previous) => {
        const next = new Set(previous);
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        return next;
      });
    },
    [enabled],
  );

  const toggleAll = useCallback(() => {
    if (!enabled || selectableIds.length === 0) return;

    setSelectedIds((previous) => {
      const everySelected = selectableIds.every((id) => previous.has(id));
      if (everySelected) return new Set();
      return new Set(selectableIds);
    });
  }, [enabled, selectableIds]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const getSelectedIds = useCallback(() => {
    if (!enabled) return [];
    return [...selectedIds].filter((id) => selectableIdSet.has(id));
  }, [enabled, selectedIds, selectableIdSet]);

  return {
    enabled,
    selectedCount,
    allSelected,
    someSelected,
    isSelected,
    toggleItem,
    toggleAll,
    clearSelection,
    getSelectedIds,
  };
}
